import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { InitialItemsQuery } from '../../graphql/graphql';
import { ItemCard } from '../ui/ItemCard';
import { CardsLayout } from '../ui/CardsLayout';
import { NewSection } from '../NewSection';
import { getInitialItems } from '../../api/queries';
import { ItemsPlaceholder } from '../Placeholders/ItemsPlaceholder';
import { ITEMS_LIMIT } from '../../utils/constants/homePage';
import { SectionError } from '../SectionError/SectionError';

interface Props {
  isBookmarked: (id: string) => boolean;
}

export const AgenciesSection: React.FC<Props> = ({ isBookmarked }) => {
  const { t } = useTranslation();
  const { data, loading, error, refetch } = useQuery<InitialItemsQuery>(
    getInitialItems,
    {
      variables: {
        limit: ITEMS_LIMIT,
        offset: 0
      }
    }
  );

  const filteredAgencies = useMemo(
    () =>
      (data?.agencies?.edges ?? [])
        .map(edge => edge?.node)
        .filter(
          (agency): agency is NonNullable<typeof agency> =>
            !!agency && agency.items.length > 0
        ),
    [data?.agencies]
  );

  const agenciesList = useMemo(
    () =>
      filteredAgencies.map((agency, index) => {
        const firstItem = agency.items[0];

        return (
          <ItemCard
            key={firstItem.id}
            cypress="agency-card-link"
            hasHorizontalScroll
            {...firstItem}
            inBookmarks={isBookmarked(firstItem.id)}
            isBusiness
            priority={index < 4}
            stat="item_card_home_agency"
          />
        );
      }),
    [filteredAgencies, isBookmarked]
  );

  const content = error ? (
    <SectionError onRetry={() => refetch()} />
  ) : (
    <CardsLayout hasHorizontalScroll limitMobileItems>
      {agenciesList}
    </CardsLayout>
  );

  return (
    <NewSection
      id="home-page-agencies"
      title={t('sections.agencies.title')}
      url="/agentlikler"
      asLink="/agentlikler"
      urlTitle={t('sections.agencies.show_all')}
      cypress="agencies-section-title"
      hasHorizontalScroll={!!agenciesList.length && !error}
    >
      {!(loading && !agenciesList.length) ? (
        content
      ) : (
        <ItemsPlaceholder isCardsLayout noHeader />
      )}
    </NewSection>
  );
};
